import { generateId } from '../helpers';
import { isForceInvestActive, FORCE_INVEST_CONTRACT_VALUE_CAP } from '../../data/depotEconomics';
import { checkAssignmentHseBlock } from '../helpers/hseCompliance';
import { frigoZoneBlocked } from '../../utils/contractCompatibility';
import { evaluateContractCoverage, depotCoversContract } from '../../data/depotGeo';

// Permit hierarchy (kept in sync with ContractAssignModal): higher classes
// imply lower ones. B → C1 → C → CE.
const _PERMIT_RANK = { B: 0, C1: 1, C: 2, CE: 3 };

function _driverCanDriveVehicle(driver, vehicle) {
  if (!driver || !vehicle) return true;
  const required = vehicle.license || 'B';
  const owned = driver.licenses || ['B'];
  if (owned.includes(required)) return true;
  const reqRank = _PERMIT_RANK[required] ?? 0;
  const bestOwned = owned.reduce((acc, lic) => Math.max(acc, _PERMIT_RANK[lic] ?? 0), 0);
  return bestOwned >= reqRank;
}

export const createContractSlice = (set, get) => ({
  activeContracts: [],
  availableContracts: [],
  // 17/06/2026 — Rémi Forni v2 : queue des contrats acceptés en différé
  // pendant qu'un camion est en transfert inter-dépôts. Promus en
  // activeContracts à l'arrivée du camion (cf. promotePendingTransferContracts
  // appelé dans nextDay après tickVehicles).
  // Shape : [{ contractInstanceId, contract, vehicleId, driverId,
  //            priceMultiplier, argumentationOutcome, arrivalDay,
  //            transferCost, queuedOnDay }, ...]
  pendingTransferContracts: [],

  /**
   * 11/06/2026 (soir 9) — Memorize the player's argumentation choice for a
   * specific client. Subsequent contracts from that client will skip the
   * ContractArgumentationModal and auto-apply the saved choice.
   *
   * @param {string} clientId
   * @param {'prix'|'délais'|'qualité'|'proximité'} choice
   */
  rememberClientArgumentationChoice: (clientId, choice) => {
    if (!clientId || !choice) return;
    set(state => ({
      clientArgumentationDefaults: {
        ...(state.clientArgumentationDefaults || {}),
        [clientId]: { choice, savedAt: state.totalDays || 0 },
      },
    }));
  },

  /**
   * Reset the memorized choice for one client (player picks "Changer
   * l'argumentation" on the contract card). Next acceptance from that
   * client will reopen the modal.
   */
  resetClientArgumentationChoice: (clientId) => {
    if (!clientId) return;
    set(state => {
      const next = { ...(state.clientArgumentationDefaults || {}) };
      delete next[clientId];
      return { clientArgumentationDefaults: next };
    });
  },

  acceptContract: (contractInstanceId, vehicleId, driverId, options = {}) => {
    const state = get();
    const contract = state.availableContracts.find(c => c.instanceId === contractInstanceId);
    if (!contract) return;

    // 03/06/2026 Phase 3 bis — Argumentation mini-game multiplier.
    // The Dashboard passes options.priceMultiplier (0.95 / 1 / 1.05) from
    // ContractArgumentationModal. Applied to revenue + dailyRevenue + totalValue.
    const priceMultiplier = (typeof options.priceMultiplier === 'number' && options.priceMultiplier > 0)
      ? options.priceMultiplier : 1;

    // 04/06/2026 — Argumentation history tracking (commercial KPIs).
    // Each acceptance produces a stat entry { day, outcome, multiplier }
    // consumed by the "Performance commerciale" widget on /strategie.
    // We keep at most the last 100 entries to bound memory.
    const argumentationOutcome = options.argumentationOutcome || (
      priceMultiplier > 1 ? 'success'
      : priceMultiplier < 1 ? 'failure'
      : 'neutral'
    );

    // 31/05/2026 — Vehicle delivery gate. A vehicle still in its 3-day
    // delivery cycle (status === 'delivery') cannot be assigned. The UI
    // already greys out the option but we enforce here too. Same for a
    // vehicle in the 5-day resale cycle (status === 'selling').
    const vehicleEarly = state.vehicles.find(v => v.id === vehicleId);
    if (vehicleEarly && vehicleEarly.status === 'delivery') {
      return {
        ok: false,
        reason: 'vehicle_in_delivery',
        availableOnDay: vehicleEarly.availableOnDay || (state.totalDays + 3),
      };
    }
    if (vehicleEarly && vehicleEarly.status === 'selling') {
      return {
        ok: false,
        reason: 'vehicle_in_resale',
        saleCompletionDay: vehicleEarly.saleCompletionDay || (state.totalDays + 5),
      };
    }

    // 24/07/2026 — Contrats locaux export : l'affectation exige un
    // chauffeur LOCAL du pays du contrat (recruté via le vivier local RH).
    if (contract.requiresLocalCountry) {
      const drv = (state.drivers || []).find(d => d.id === driverId);
      if (!drv || drv.localCountry !== contract.requiresLocalCountry) {
        return { ok: false, reason: 'requires_local_driver', country: contract.requiresLocalCountry };
      }
    }

    // 28/05/2026 — Force-investment rule : when the player runs a single
    // depot but has > 8 vehicles, the saturation forbids signing big
    // contracts (capacity & dock constraints). Refused contracts stay in
    // the available list so the player can still accept them later — once
    // they've opened a 2nd depot.
    if (isForceInvestActive(state) && (contract.totalValue || contract.revenue || 0) > FORCE_INVEST_CONTRACT_VALUE_CAP) {
      return { ok: false, reason: 'force-invest-cap' };
    }

    // 13/06/2026 — Obligation zone frigorifique au dépôt pour les contrats
    // frigo T3+. L'UI grise déjà le bouton, on garde la garde slice-level
    // pour tout autre appelant (scripts, tests, événements).
    if (frigoZoneBlocked(contract, state.activeInvestments, state.ownedDepots)) {
      return { ok: false, reason: 'frigo_zone_required' };
    }

    // 29/05/2026 — Hard permit gate. The UI already blocks the confirm
    // button when the driver lacks the required permit, but we also guard
    // here so any future caller (event-driven re-assignment, script,
    // tests) cannot bypass the rule.
    const vehicleObj = state.vehicles.find(v => v.id === vehicleId);
    const driverObj = state.drivers.find(d => d.id === driverId);
    if (!_driverCanDriveVehicle(driverObj, vehicleObj)) {
      return {
        ok: false,
        reason: 'driver_missing_permit',
        required: vehicleObj?.license || 'B',
        owned: driverObj?.licenses || ['B'],
      };
    }

    // 01/06/2026 — HSE soft block (Phase 5, Claire Maréchal). Medical
    // expired → driver cannot drive ANY vehicle. ADR expired AND vehicle
    // requires ADR → driver cannot drive THAT vehicle. FCO/CACES are
    // narrative-only and don't block assignment.
    const hseBlock = checkAssignmentHseBlock(driverObj, vehicleObj, state.totalDays || 0);
    if (hseBlock.blocked) {
      return {
        ok: false,
        reason: hseBlock.reason,
        trainingKey: hseBlock.trainingKey,
        expiresOn: hseBlock.expiresOn,
      };
    }

    // 04/06/2026 — Vehicle/driver conflict detection. If the vehicle is
    // already on an ACTIVE contract (or the driver is already assigned to
    // another active contract), we refuse the assignment unless the caller
    // explicitly passes `options.force = true`. The UI surfaces a
    // confirmation modal listing the contracts that would be displaced
    // BEFORE re-attempting with force.
    const conflictingVehicleContract = state.activeContracts.find(
      c => c.vehicleId === vehicleId
    );
    const conflictingDriverContract = state.activeContracts.find(
      c => c.driverId === driverId
    );
    if (!options.force && (conflictingVehicleContract || conflictingDriverContract)) {
      return {
        ok: false,
        reason: 'assignment_conflict',
        vehicleConflict: conflictingVehicleContract
          ? { instanceId: conflictingVehicleContract.instanceId, name: conflictingVehicleContract.name, clientName: conflictingVehicleContract.clientName, daysRemaining: conflictingVehicleContract.daysRemaining }
          : null,
        driverConflict: conflictingDriverContract
          ? { instanceId: conflictingDriverContract.instanceId, name: conflictingDriverContract.name, clientName: conflictingDriverContract.clientName, daysRemaining: conflictingDriverContract.daysRemaining }
          : null,
      };
    }

    // 13/06/2026 — Chantier Dépôts Phase 1 : couverture géographique.
    // Contrat hors du rayon d'action de tous les dépôts → pénalité de
    // revenus variable ≥ 40 % (sous-traitance locale, trajets à vide).
    // Évaluée à l'acceptation : ouvrir un dépôt dans la région AVANT
    // d'accepter supprime la pénalité.
    const coverage = evaluateContractCoverage(contract, state.ownedDepots, state.company, state.activeInvestments);
    const zoneMultiplier = coverage.covered ? 1 : (1 - coverage.penalty);

    // 13/06/2026 — Phase 3 : si le contrat est couvert par le réseau, le
    // véhicule choisi doit appartenir à un dépôt qui couvre la ville
    // d'origine (sinon : transférer un camion ou utiliser l'autre dépôt).
    if (coverage.covered && contract.originCityId) {
      const vDepot = (state.ownedDepots || []).find(d => d.id === (vehicleObj.depotId || 'depot-hq'));
      if (vDepot && !depotCoversContract(vDepot, contract, state.company, state.activeInvestments)) {
        // 17/06/2026 (Lot 4 — v2) — Rémi Forni (role-christophe-faure)
        // automatise les transferts inter-dépôts de manière TRANSPARENTE :
        // 1) on initie le transfert du camion compatible (autoTransferForContract)
        // 2) on enregistre le contrat en file `pendingTransferContracts` avec
        //    les choix véhicule/chauffeur du joueur + prix négocié
        // 3) à l'arrivée du camion (J+1, géré par tickVehicles), le contrat
        //    est promu en `activeContracts` via promotePendingTransferContracts.
        // L'apprenant peut donc accepter n'importe quel contrat hors zone
        // sans intervention manuelle — Rémi prend la main de bout en bout.
        const hasRemi = !!(state.hiredSupport || {})['role-christophe-faure'];
        const targetDepotId = coverage.nearestDepotId;
        if (hasRemi && targetDepotId && typeof get().autoTransferForContract === 'function') {
          const autoRes = get().autoTransferForContract(vehicleId, targetDepotId, contract);
          if (autoRes && autoRes.ok) {
            // Queue the contract for promotion when the truck arrives.
            // Keep the player's choices (driver, argumentation) so the
            // finalization is faithful to what they signed up for.
            set((s) => ({
              pendingTransferContracts: [
                ...(s.pendingTransferContracts || []),
                {
                  contractInstanceId: contract.instanceId,
                  contract: { ...contract }, // snapshot — survives if availableContracts rotates
                  vehicleId: autoRes.vehicleId,
                  driverId,
                  priceMultiplier,
                  argumentationOutcome,
                  arrivalDay: autoRes.arrivalDay,
                  transferCost: autoRes.cost,
                  queuedOnDay: s.totalDays || 0,
                },
              ],
              // Remove the contract from `availableContracts` so it can't be
              // double-accepted while in transit. It'll come back as
              // `activeContracts` once the truck lands.
              availableContracts: (s.availableContracts || []).filter(
                c => c.instanceId !== contract.instanceId,
              ),
            }));
            return {
              ok: true,
              deferred: true,
              reason: 'remi_auto_transfer',
              requiredDepotId: targetDepotId,
              transferredVehicleId: autoRes.vehicleId,
              arrivalDay: autoRes.arrivalDay,
              cost: autoRes.cost,
            };
          }
        }
        return { ok: false, reason: 'vehicle_wrong_depot', requiredDepotId: targetDepotId };
      }
    }

    const activeContract = {
      ...contract,
      vehicleId,
      driverId,
      daysRemaining: contract.frequency === 'ponctuel' ? (contract.duration || 1) : (contract.duration || 30),
      dailyRevenue: Math.round((contract.revenue || contract.dailyRevenue) * priceMultiplier * zoneMultiplier),
      revenue: contract.revenue ? Math.round(contract.revenue * priceMultiplier * zoneMultiplier) : contract.revenue,
      totalValue: contract.totalValue ? Math.round(contract.totalValue * priceMultiplier * zoneMultiplier) : contract.totalValue,
      argumentationMultiplier: priceMultiplier !== 1 ? priceMultiplier : undefined,
      distanceKm: coverage.distanceKm || contract.distanceKm,
      outOfZonePenalty: coverage.covered ? undefined : coverage.penalty,
      servingDepotId: coverage.nearestDepotId || undefined,
      startDay: state.day,
      startMonth: state.month,
    };

    // Detach driver from any other vehicle, then attach to the chosen one.
    // Detach the chosen vehicle's previous driver (if different) and set the new driver.
    const updatedDrivers = state.drivers.map(d => {
      if (d.id === driverId) {
        return { ...d, assignedVehicleId: vehicleId, status: 'active' };
      }
      if (d.assignedVehicleId === vehicleId) {
        // Previous driver of this vehicle — release them.
        return { ...d, assignedVehicleId: null, status: 'idle' };
      }
      return d;
    });

    const previousVehicleIdOfDriver = state.drivers.find(d => d.id === driverId)?.assignedVehicleId;
    const updatedVehicles = state.vehicles.map(v => {
      if (v.id === vehicleId) {
        return {
          ...v, status: 'active', assignedContractId: contract.instanceId, assignedDriverId: driverId,
          // 22/07/2026 — rapport Flotte Forni : typologie des contrats
          // acceptés avec ce camion (catégorie → nombre).
          contractTypes: {
            ...(v.contractTypes || {}),
            [contract.category || 'Autre']: ((v.contractTypes || {})[contract.category || 'Autre'] || 0) + 1,
          },
        };
      }
      if (v.id === previousVehicleIdOfDriver && previousVehicleIdOfDriver !== vehicleId) {
        // Driver was tied to a different vehicle — clear that link.
        return { ...v, assignedDriverId: null };
      }
      return v;
    });

    // Extra-UE customs fees: deduct immediately as an exceptional charge.
    // This models the upfront DAU + EUR1 + transit costs that the carrier
    // must outlay before the freight even moves.
    const customsCost = (contract.international === 'extra' && contract.customsFees > 0)
      ? contract.customsFees
      : 0;

    // 04/06/2026 — When force=true, ALSO remove the displaced contracts
    // from activeContracts (they were keeping the vehicle/driver busy). The
    // player has confirmed they accept losing them. We collect their ids
    // here so the set() below filters them out atomically.
    const displacedContractIds = new Set();
    if (conflictingVehicleContract && conflictingVehicleContract.instanceId !== contractInstanceId) {
      displacedContractIds.add(conflictingVehicleContract.instanceId);
    }
    if (conflictingDriverContract
        && conflictingDriverContract.instanceId !== contractInstanceId
        && conflictingDriverContract.instanceId !== conflictingVehicleContract?.instanceId) {
      displacedContractIds.add(conflictingDriverContract.instanceId);
    }
    const displacedContracts = state.activeContracts.filter(c => displacedContractIds.has(c.instanceId));

    set({
      activeContracts: [
        ...state.activeContracts.filter(c => !displacedContractIds.has(c.instanceId)),
        activeContract,
      ],
      availableContracts: state.availableContracts.filter(c => c.instanceId !== contractInstanceId),
      vehicles: updatedVehicles,
      drivers: updatedDrivers,
      treasury: state.treasury - customsCost,
      monthlyExpenses: (state.monthlyExpenses || 0) + customsCost,
      // 04/06/2026 — Track each negotiation outcome for the commercial
      // performance widget. Keep last 100 entries.
      argumentationHistory: [
        ...((state.argumentationHistory || []).slice(-99)),
        {
          day: state.totalDays || 0,
          outcome: argumentationOutcome,
          multiplier: priceMultiplier,
          clientName: contract.clientName || null,
          contractName: contract.name,
        },
      ],
      eventLog: customsCost > 0 ? [...(state.eventLog || []), {
        id: generateId(),
        type: 'customs',
        instanceId: contract.instanceId + '-customs',
        title: 'Dédouanement export',
        choiceMade: `Frais douaniers — ${contract.name}`,
        resolvedAt: { day: state.day, month: state.month, year: state.year, totalDays: state.totalDays },
        netCashImpact: -customsCost,
        eventCost: customsCost,
        eventBonus: 0,
      }] : (state.eventLog || []),
      // 04/06/2026 — Notify the player for each displaced contract so they
      // see in Messagerie / notifications why the contract disappeared.
      notifications: [
        ...(state.notifications || []).slice(-30),
        ...displacedContracts.map(dc => {
          // Lazy require to avoid pulling i18n into a hot-path slice import.
          // tStandalone reads the current language from useLanguageStore.
          let title = 'Contrat interrompu';
          let message = `Le contrat « ${dc.name} » a été interrompu suite à la réassignation de son véhicule/chauffeur. Réputation -3.`;
          try {
            const { tStandalone } = require('../../i18n');
            title = tStandalone('assignment_conflict_notif_title');
            message = tStandalone('assignment_conflict_notif_message', { name: dc.name });
          } catch (err) { /* fall through to default FR strings */ }
          return {
            id: generateId(),
            type: 'contract_displaced',
            title,
            message,
            day: state.totalDays,
            read: false,
          };
        }),
      ],
      // Each displaced contract costs 3 reputation points (lighter than a
      // full cancellation at -5; the player took the conscious decision).
      reputation: Math.max(0, (state.reputation || 0) - 3 * displacedContracts.length),
    });

    // CRM: every displaced contract counts as a soft cancellation.
    displacedContracts.forEach(dc => {
      if (dc.clientId && typeof get().applyClientEvent === 'function') {
        try { get().applyClientEvent(dc.clientId, 'contract_cancelled', { contractId: dc.instanceId, contractName: dc.name }); }
        catch (err) { console.warn('[crm] displaced contract event failed:', err); }
      }
    });

    // 08/02/2026 — Append to the contract history ledger (status: accepted).
    // Best-effort : if the history slice isn't wired yet (older save), skip.
    if (typeof get().logContractAccepted === 'function') {
      try {
        get().logContractAccepted(activeContract, {
          day: state.totalDays || 0,
          vehicleId,
          driverId,
          revenue: activeContract.revenue,
          dailyRevenue: activeContract.dailyRevenue,
          multiplier: priceMultiplier,
        });
      } catch (err) { console.warn('[contract-history] accepted log failed:', err); }
    }
    if (displacedContracts.length > 0 && typeof get().logContractsDisplaced === 'function') {
      try {
        get().logContractsDisplaced(displacedContracts.map(c => c.instanceId), state.totalDays || 0);
      } catch (err) { console.warn('[contract-history] displaced log failed:', err); }
    }

    // 04/06/2026 — Spot exclusivity: if the accepted contract was an
    // exclusive spot call, bump the engagement's completedCalls counter.
    if (contract.spotCall && typeof get().onSpotCallAccepted === 'function') {
      try { get().onSpotCallAccepted(activeContract.instanceId); }
      catch (err) { console.warn('[spotEngagement] onSpotCallAccepted failed:', err); }
    }

    // Post a message to brief the user on the customs cost they just paid.
    if (customsCost > 0) {
      try {
        const pushMsg = get().pushCustomMessage;
        if (pushMsg) {
          pushMsg({
            senderType: 'urssaf',
            senderName: 'Service des Douanes',
            tone: 'formal',
            kind: 'info',
            subject: `Frais de dédouanement export ${contract.country || 'Extra-UE'}`,
            body: `Pour le contrat « ${contract.name} » (${contract.incoterm}), les frais de dédouanement, transit et formalités EUR1 ont été prélevés : ${customsCost.toLocaleString('fr-FR')} €. La TVA est exonérée (export hors UE) mais ces frais sont à intégrer dans votre calcul de marge.`,
            ttlDays: 14,
          });
        }
      } catch (err) { console.warn('[contract] customs message push failed:', err); }
    }
  },

  /**
   * Cancel an active contract. The player pays a 25% penalty on the remaining
   * gross revenue (daysRemaining × dailyRevenue), the vehicle + driver are
   * freed, and reputation drops by 5 points (dissuasive).
   */
  cancelContract: (contractInstanceId) => {
    const state = get();
    const contract = state.activeContracts.find(c => c.instanceId === contractInstanceId);
    if (!contract) return false;

    const remainingRevenue = (contract.daysRemaining || 0) * (contract.dailyRevenue || 0);
    const penalty = Math.round(remainingRevenue * 0.25);

    // Free the vehicle: clear its contract binding and bring it back to idle.
    // Also detach its driver so both are immediately reassignable.
    const updatedVehicles = state.vehicles.map(v =>
      v.assignedContractId === contractInstanceId
        ? { ...v, status: 'idle', assignedContractId: null, assignedDriverId: null }
        : v
    );

    // Free the driver tied to that vehicle / contract (symmetric with the
    // vehicle cleanup). Driver returns to 'idle'.
    const updatedDrivers = state.drivers.map(d =>
      d.id === contract.driverId
        ? { ...d, assignedVehicleId: null, status: 'idle' }
        : d
    );

    const notification = {
      id: generateId(),
      type: 'contract_cancelled',
      title: 'Contrat annulé',
      message: `Vous avez résilié "${contract.name}". Pénalité : ${penalty.toLocaleString('fr-FR')} € — Réputation -5.`,
      day: state.totalDays,
      read: false,
    };

    set({
      activeContracts: state.activeContracts.filter(c => c.instanceId !== contractInstanceId),
      vehicles: updatedVehicles,
      drivers: updatedDrivers,
      treasury: state.treasury - penalty,
      reputation: Math.max(0, (state.reputation || 0) - 5),
      notifications: [...(state.notifications || []).slice(-30), notification],
    });
    // 08/02/2026 — Ledger : mark this contract as cancelled.
    if (typeof get().logContractCancelled === 'function') {
      try { get().logContractCancelled(contract, state.totalDays || 0); }
      catch (err) { console.warn('[contract-history] cancel log failed:', err); }
    }
    // CRM — annulation is the most severe loyalty hit (-15).
    if (contract.clientId && typeof get().applyClientEvent === 'function') {
      try { get().applyClientEvent(contract.clientId, 'contract_cancelled', { contractId: contract.instanceId, contractName: contract.name }); } catch (err) { console.warn('[crm] event apply failed:', err); }
    }
    return { penalty, remainingRevenue };
  },

  /**
   * 17/06/2026 — Rémi Forni v2 : promotion automatique des contrats en
   * file `pendingTransferContracts` une fois que le camion transféré est
   * arrivé à destination (status === 'idle' suite à tickVehicles). Appelé
   * depuis coreSlice.nextDay APRÈS tickVehicles pour que la promotion
   * voie bien l'état post-transfert.
   *
   * Pour chaque contrat en attente dont le camion est arrivé :
   *  - On rétablit le contrat dans `availableContracts` (le système
   *    d'acceptation a besoin de le trouver via instanceId)
   *  - On appelle `acceptContract(force=true)` avec le véhicule/chauffeur
   *    enregistrés au moment de la signature différée
   *  - Si l'acceptation échoue (chauffeur indisponible, conflit, etc.),
   *    on logue et on retire le contrat de la file pour ne pas boucler
   *
   * @returns {{ promoted: number, failed: number }}
   */
  promotePendingTransferContracts: () => {
    const state = get();
    const pending = state.pendingTransferContracts || [];
    if (pending.length === 0) return { promoted: 0, failed: 0 };

    const today = state.totalDays || 0;
    const remaining = [];
    let promoted = 0;
    let failed = 0;
    const notificationsToAdd = [];

    pending.forEach((entry) => {
      // Le camion est-il arrivé ? Status repassé à 'idle' par tickVehicles.
      const vehicle = (get().vehicles || []).find(v => v.id === entry.vehicleId);
      if (!vehicle) {
        // Camion disparu (vendu ? cassé ?) — on abandonne le contrat différé.
        failed += 1;
        notificationsToAdd.push({
          id: generateId(),
          type: 'pending_transfer_lost',
          title: 'Contrat différé perdu',
          message: `Le contrat "${entry.contract.name}" n'a pas pu être signé : le camion réservé n'est plus disponible.`,
          day: today,
          read: false,
        });
        return;
      }
      if (vehicle.status === 'transfer') {
        // Camion encore en route : on garde l'entrée pour la prochaine tick.
        remaining.push(entry);
        return;
      }
      // Le camion est arrivé. On réinjecte le contrat dans availableContracts
      // (snapshot) et on signe en force pour ignorer la vérification de
      // couverture (la nouvelle position du camion couvre désormais la zone).
      set((s) => ({
        availableContracts: [...(s.availableContracts || []), entry.contract],
      }));
      const res = get().acceptContract(
        entry.contractInstanceId,
        entry.vehicleId,
        entry.driverId,
        {
          priceMultiplier: entry.priceMultiplier,
          argumentationOutcome: entry.argumentationOutcome,
          force: true,
        },
      );
      if (res && (res.ok === true || res.ok === undefined)) {
        promoted += 1;
        notificationsToAdd.push({
          id: generateId(),
          type: 'pending_transfer_signed',
          title: 'Contrat différé signé',
          message: `Rémi Forni a finalisé l'acceptation de "${entry.contract.name}" : votre camion est arrivé au bon dépôt.`,
          day: today,
          read: false,
        });
      } else {
        failed += 1;
        // Nettoyer : retirer le contrat à nouveau d'availableContracts si
        // l'acceptation a échoué (sinon il apparaîtrait dans la liste).
        set((s) => ({
          availableContracts: (s.availableContracts || []).filter(
            c => c.instanceId !== entry.contractInstanceId,
          ),
        }));
        notificationsToAdd.push({
          id: generateId(),
          type: 'pending_transfer_failed',
          title: 'Contrat différé impossible',
          message: `Le contrat "${entry.contract.name}" n'a pas pu être signé après l'arrivée du camion (${res?.reason || 'raison inconnue'}).`,
          day: today,
          read: false,
        });
      }
    });

    set((s) => ({
      pendingTransferContracts: remaining,
      notifications: [
        ...(s.notifications || []).slice(-30),
        ...notificationsToAdd,
      ],
    }));
    return { promoted, failed };
  },
});
